import { Button, Checkbox, Drawer, Dropdown, Input, Menu, Modal, Select, Switch, TimePicker } from "antd";
import "./ScreenSetUpModal.scss";
import React, { ReactNode, useEffect, useState } from "react";
import DSAApi from "../../api/DSAApi";
import { handleServerError } from "../../util/ErrorHandler";
import { DsaScreenDto, LocationDto } from "../../generated/dashboard-client";
import { useRecoilState, useRecoilValue } from "recoil";
import { locationsState, memberState, mobileState } from "../../state/atoms";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import DisplayIcon from "../icons/DisplayIcon";
import { CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import moment from "moment";

const { Option } = Select;

enum View { HavePlayer, SetUp }

const Days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export interface ScreenSetUpModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSaved: (device: DsaScreenDto) => void;
  seeHelp: () => void;
  withTooltip: (tooltip: string, object: ReactNode) => ReactNode;
}

export function ScreenSetUpModal({ isVisible, onClose, onSaved, seeHelp, withTooltip }: ScreenSetUpModalProps) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [placement, setPlacement] = useState("");
  const [loc, setLoc] = useState({} as LocationDto);
  const [timeZone, setTimeZone] = useState("");
  const [orientation, setOrientation] = useState("");
  const [hasReboot, setReboot] = useState(false);
  const [rebootDays, setRebootDays] = useState(Days.map(day => ({ day, selected: false })));
  const [rebootTime, setRebootTime] = useState('');
  const [loading, setLoading] = useState(false);

  const [locations] = useRecoilState(locationsState);
  const [member] = useRecoilState(memberState);
  const mobile = useRecoilValue(mobileState);

  const [view, setView] = useState(View.HavePlayer);

  useEffect(() => {
    setLoc({});
    if (sessionStorage.getItem('setup-after-locadd')) {
      const closestDate = Math.max(...locations.map(loc => Date.parse(loc.createdDate!)));
      setLoc(locations.find(loc => Date.parse(loc.createdDate!) === closestDate)!);
      sessionStorage.removeItem('setup-after-locadd');
    } else {
      if (locations.length === 1) {
        setLoc(locations[0]);
      }
    }
  }, [locations])

  useEffect(() => {
    if (!isVisible) {
      setView(View.HavePlayer);
    }
  }, [isVisible])

  async function activate() {
    try {
      setLoading(true);
      let restartDay = 0;
      if (rebootDays.filter(it => it.selected).length === 1) {
        restartDay = rebootDays.findIndex(it => it.selected) + 1;
      }

      const resp = await DSAApi.activateDevice({
        name,
        placement,
        locationId: loc.internalId,
        code: code,
        deviceQueryDto: {
          serial: code
        },
        timezone: timeZone,
        rotation: parseInt(orientation),
        scheduledReboot: hasReboot,
        restartDay: restartDay,
        restartTime: `${rebootTime}:00`
      });
      if (!member.enterpriseDisplayAccount) {
        setLoading(false);
      }
      onSaved(resp.data);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }

  function toggleRebootDay(ev: CheckboxChangeEvent, day: string) {
    ev.stopPropagation();
    setRebootDays([...rebootDays.map(it => {
      it.selected = it.day === day;
      return it;
    })]);
  }

  function rebootAll() {
    rebootDays.forEach(it => it.selected = true);
    setRebootDays([...rebootDays]);
  }

  function addNewLocation() {
    window.MHM.ProModal.addLocation({
      memberId: member.id,
      onSuccess: () => {
        sessionStorage.setItem('setup-after-locadd', 'true');
        window.location.reload();
      }
    })
  }

  function onLocClick(val: string) {
    if (val === 'new-loc') {
      setLoc({});
      addNewLocation();
    } else {
      setLoc(locations.find(loc => loc.internalId === parseInt(val))!);
    }
  }

  function onRebootTimeOpenChange(open: boolean) {
    if (!open) {
      const timeValue = document.getElementById('rebootTimePicker')?.getAttribute('value');
      if (timeValue) {
        setRebootTime(moment(timeValue, 'hh:mm A').format('HH:mm'));
      }
    }
  }

  function setRebootToNow() {
    setRebootTime(moment().format('HH:mm'));
    setTimeout(() => {
      document.querySelectorAll('.ant-picker-ok .ant-btn').forEach(btn => {
        (btn as HTMLElement).click();
      })
    }, 50);
  }

  const RebootDaysMenu = <Menu>
    <div className={"reboots"}>
      <Button onClick={rebootAll}>Reboot Daily</Button>
      <ul>
        {rebootDays.map(it => <li key={it.day}>
          <Checkbox checked={it.selected} onChange={(ev) => toggleRebootDay(ev, it.day)}
            onClick={ev => ev.stopPropagation()} />
          {it.day}
        </li>)}
      </ul>
    </div>
  </Menu>;

  const content = (
  view === View.HavePlayer ? <>
    <header>
      <CloseOutlined onClick={() => onClose()} className="close-btn" />
    </header>
    <div className="have-player">
      <h3>Have the MustHaveMenus player?</h3>
      <p>Setup requires a MustHaveMenus player to be connected to your TV screen.</p>
    </div>
    {member.enterpriseDisplayAccount && <div>
      <p>A new license will be added to your subscription after the pairing is completed.</p>
    </div>}
    <footer>
      <Button className={"primary"} size={"large"} loading={loading} onClick={() => setView(View.SetUp)}>
        Ok
      </Button>
      <a href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029537-skykit-player-device-setup" target="_blank" rel="noreferrer">Help</a>
    </footer>
  </>
  :
  <>
    <header>
      <DisplayIcon />
      <span className="title-help">
        <h2>Set up your screen</h2>
        {withTooltip("Help", <QuestionCircleOutlined onClick={seeHelp} />)}
      </span>
      <p>Enter the code displayed on your TV screen.</p>
      <CloseOutlined onClick={() => onClose()} className="close-btn" />
    </header>

    <div className={"split"}>
      <div>
        <Input size={"large"} placeholder={"Pairing code"}
          value={code} onChange={ev => setCode(ev.target.value)} />
      </div>

      <div>
        <Input size={"large"} placeholder={"Name your screen"}
          value={name} onChange={ev => setName(ev.target.value)} />
      </div>
    </div>

    <div className={"split"}>
      <div>
        <Select
          size={"large"}
          placeholder="Location Assignment"
          onSelect={(val: any) => onLocClick(val)}
          value={loc.name}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        >
          {member.owner && <Option className="new-loc" key="new-loc" onClick={() => onLocClick('')}>
            + Create New Location
          </Option>}
          {locations.map(it => <Option key={it.internalId}>
            {it.name}
          </Option>)}
        </Select>
      </div>
      <div>
        <Input size={"large"} placeholder={"Placement"}
          value={placement} onChange={ev => setPlacement(ev.target.value)} />
      </div>
    </div>

    <div className={"split"}>
      <div>
        <Select size={"large"} placeholder="Time Zone" onChange={val => setTimeZone(val)}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <Option key="America/Los_Angeles">Pacific Time - Los Angeles</Option>
          <Option key="America/Phoenix">Mountain Time - Phoenix</Option>
          <Option key="America/Denver">Mountain Time - Denver</Option>
          <Option key="America/Chicago">Central Time - Chicago</Option>
          <Option key="America/New_York">Eastern Time - New York</Option>
          <Option key="America/Anchorage">Alaska Time - Anchorage</Option>
          <Option key="Pacific/Honolulu">Hawaii Time - Honolulu</Option>
        </Select>
      </div>
      <div>
        <Select size={"large"} placeholder="Orientation" onChange={val => setOrientation(val)}
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
          <Option key="0">0° Landscape</Option>
          <Option key="90">90° Portrait</Option>
          <Option key="270">180° Portrait Flipped</Option>
        </Select>
      </div>
    </div>

    <div className={"reboot"}>
      <div className={"hd"}>
        <label>Schedule Reboot</label>
        <Switch checked={hasReboot} onChange={() => setReboot(!hasReboot)} />
      </div>

      {!hasReboot && <div className={"cover"}></div>}

      <div className={`split`}>
        <div>
          <label>Reboot Day</label>
          <Dropdown overlay={RebootDaysMenu}>
            <Button size={"large"}>
              {rebootDays.filter(it => it.selected).length === 0 ? <span className="not-selected">Select Day</span> :
                rebootDays.filter(it => it.selected).length === 1 ? rebootDays.find(it => it.selected)!.day :
                  'Daily'}
            </Button>
          </Dropdown>
        </div>
        <div>
          <label>Reboot Time</label>
          <TimePicker
            id="rebootTimePicker"
            placeholder={"Select Time"}
            suffixIcon={null}
            value={rebootTime ? moment(rebootTime, "HH:mm") : null}
            format="hh:mm A"
            use12Hours={true}
            minuteStep={15}
            onChange={(time, str) => setRebootTime(str ? moment(str, 'hh:mm A').format('HH:mm') : '')}
            onOpenChange={(open) => onRebootTimeOpenChange(open)}
            showNow={false}
            renderExtraFooter={() => (<div>
              <span onClick={() => setRebootToNow()}>Now</span>
            </div>)}
          />
        </div>
      </div>
    </div>

    <footer>
      <Button className={"primary"} size={"large"} loading={loading} onClick={activate} disabled={!code || !name || !loc || !orientation || !timeZone || (hasReboot && (rebootDays.filter(it => it.selected).length === 0 || !rebootTime))}>
        Activate
      </Button>
      <a className="help-link" href="https://intercom.help/musthavemenus-289b0117ae34/en/articles/11029539-skykit-player-pairing" target="_blank" rel="noreferrer">
        {mobile && <QuestionCircleOutlined/>}
        Help
      </a>
    </footer>
  </>)

  return (
    !mobile ?
    <Modal
      className={"clean screen-set-up"}
      visible={isVisible}
      onCancel={() => onClose()}
    >
      {content}
    </Modal>
    :
    <Drawer
      placement={"bottom"}
      visible={isVisible}
      className={"mobile-bottom-menu screen-set-up"}
      height={"80vh"}
      closeIcon={false}
      maskClosable={true}
      onClose={() => onClose()}
    >
      {content}
    </Drawer>
  );
}
